import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../state/state';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import { closeModal, toggleLoading } from '../../../state/ModalSlice/ModalSlice';
import { db } from '../../../Config/firebase';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { ExportType } from '../types/ExportTypes';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2';

const ExportAttendanceModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state) => state.ModalSlice.ExportAttendanceModalOpen);
    const providerId: string = useAppSelector(selectEffectiveUID)    
    const isLoading = useAppSelector((state) => state.ModalSlice.isLoading);

    const today = new Date();
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth());

    const currentYear = today.getFullYear();
    
    // Get first and last day of selected month
    const getMonthRange = (month: number) => {
        const firstDay = dayjs().year(currentYear).month(month).startOf('month');
        const lastDay = dayjs().year(currentYear).month(month).endOf('month');
        return { firstDay, lastDay };
    };

    const { firstDay: firstDayOfMonth, lastDay: lastDayOfMonth } = getMonthRange(selectedMonth);


    const handleMonthChange = (event: any) => {
        const newMonth = event.target.value;
        setSelectedMonth(newMonth);
        
        // Update date range to first and last day of selected month
        const { firstDay: newFirstDay, lastDay: newLastDay } = getMonthRange(newMonth);
        
        setFormData({
            StartDate: newFirstDay.format('YYYY-MM-DD'),
            EndDate: newLastDay.format('YYYY-MM-DD'),
        });
    };

    const [formData, setFormData] = useState({
        StartDate: firstDayOfMonth.format('YYYY-MM-DD'),
        EndDate: lastDayOfMonth.format('YYYY-MM-DD'),
    });


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        dispatch(toggleLoading(true));
        try {
            const ExportData = {
                StartDate: Timestamp.fromDate(new Date(formData.StartDate + 'T00:00:01')),
                EndDate: Timestamp.fromDate(new Date(formData.EndDate + 'T23:59:59')),
                RequestedOn: Timestamp.fromDate(new Date()),
                Status: "Pending",
                exportType: ExportType.ATTENDANCE,
                month: selectedMonth+1,
                year: currentYear,
            };
            
            const ExportsCollectionRef = collection(db, 'ServiceProviders', providerId, 'Exports');
            await addDoc(ExportsCollectionRef, ExportData);
            handleClose();
        } catch (error) {
            console.error("Error adding export:", error);
        } finally {
            dispatch(toggleLoading(false));
        }
    };

    const handleClose = () => {
        dispatch(closeModal('ExportAttendanceModalOpen'));
        const currentMonth = today.getMonth();
        setSelectedMonth(currentMonth);
        
        const { firstDay: resetFirstDay, lastDay: resetLastDay } = getMonthRange(currentMonth);
        
        setFormData({
            StartDate: resetFirstDay.format('YYYY-MM-DD'),
            EndDate: resetLastDay.format('YYYY-MM-DD'),
        });
    };

    return (
        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },
            }}
            maxWidth="md"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className={"bg-white"}>
                <div className={"w-[550px]"}>
                    <div className={"header pb-[20px]"}>
                        <p className={"text-cf_heading font-[600]"}>Export Attendance Data</p>
                        <p className={"text-cf_small font-[400]"}>Select month and year for attendance data export</p>
                    </div>

                    <div className={"absolute right-4 top-4"}>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={"pt-0"}>
                        <Box component={"div"}>
                            <div className="flex flex-col gap-4">
                                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                    <InputLabel id="month-select-label" sx={{ fontSize: '14px' }}>Month</InputLabel>
                                    <Select
                                        labelId="month-select-label"
                                        value={selectedMonth}
                                        onChange={handleMonthChange}
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                            },
                                            '& .MuiSelect-select': {
                                                fontSize: '14px',
                                            },
                                            width: '550px'
                                        }}
                                    >
                                        {[...Array(12)].map((_, i) => (
                                            <MenuItem key={i} value={i}>
                                                {new Date(2024, i, 1).toLocaleString('default', { month: 'long' })}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="grid grid-cols-2 gap-4 items-center mb-4 max-w-[550px]">
                                        <div>
                                            <p className="text-xs font-medium mb-1 text-gray-600">Start Date</p>
                                            <DatePicker
                                                value={dayjs(formData.StartDate)}
                                                onChange={(newValue) => {
                                                    if (newValue) {
                                                        const newDate = newValue.toDate();
                                                        const endDate = new Date(formData.EndDate);
                                                        // Only update if in same month
                                                        if (newDate.getMonth() === endDate.getMonth() && 
                                                            newDate.getFullYear() === endDate.getFullYear()) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                StartDate: newValue.format('YYYY-MM-DD')
                                                            }));
                                                        }
                                                    }
                                                }}
                                                format="DD/MM/YYYY"
                                                minDate={dayjs(firstDayOfMonth.format('YYYY-MM-DD'))}
                                                maxDate={dayjs(lastDayOfMonth.format('YYYY-MM-DD'))}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        sx: {
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px',
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '14px',
                                                                },
                                                            },
                                                            width: '100%'
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium mb-1 text-gray-600">End Date</p>
                                            <DatePicker
                                                value={dayjs(formData.EndDate)}
                                                onChange={(newValue) => {
                                                    if (newValue) {
                                                        const newDate = newValue.toDate();
                                                        const startDate = new Date(formData.StartDate);
                                                        // Only update if in same month
                                                        if (newDate.getMonth() === startDate.getMonth() && 
                                                            newDate.getFullYear() === startDate.getFullYear()) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                EndDate: newValue.format('YYYY-MM-DD')
                                                            }));
                                                        }
                                                    }
                                                }}
                                                format="DD/MM/YYYY"
                                                minDate={dayjs(firstDayOfMonth.format('YYYY-MM-DD'))}
                                                maxDate={dayjs(lastDayOfMonth.format('YYYY-MM-DD'))}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        sx: {
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px',
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '14px',
                                                                },
                                                            },
                                                            width: '100%'
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </LocalizationProvider>

                                <div className={"flex gap-2 pt-4"}>
                                    <button
                                        disabled={isLoading}
                                        onClick={handleClose}
                                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    >
                                        {isLoading && <CircularProgress sx={{ color: "white" }} size={20} />}
                                        <p className={"ml-2"}>{isLoading ? "Loading" : "Start Export"}</p>
                                    </button>
                                </div>
                            </div>
                        </Box>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ExportAttendanceModal; 